import { Component, AfterViewInit, Inject } from '@angular/core';
import * as L from 'leaflet';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { GatewayService } from 'src/app/gateway.service';
import { MatSnackBar } from '@angular/material';

delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
  iconUrl: 'assets/leaflet/marker-icon.png'
});

interface Provincia {
  idprovincia: number;
  nombre: string;
}

const PROVINCIA_COORDENADAS: { [key: number]: { lat: number; lng: number; zoom: number } } = {
  1: { lat: 9.34, lng: -82.25, zoom: 9 },
  2: { lat: 8.45, lng: -80.43, zoom: 9 },
  3: { lat: 9.36, lng: -79.90, zoom: 9 },
  4: { lat: 8.43, lng: -82.43, zoom: 9 },
  5: { lat: 7.88, lng: -77.80, zoom: 8 },
  6: { lat: 7.856324, lng: -80.749266, zoom: 10 },
  7: { lat: 7.52, lng: -80.38, zoom: 10 },
  8: { lat: 9.00, lng: -79.50, zoom: 10 },
  9: { lat: 8.87, lng: -79.83, zoom: 10 },
  10: { lat: 8.12, lng: -80.97, zoom: 9 },
};

// (LIMITAR)
const PANAMA_BOUNDS = L.latLngBounds(
  L.latLng(7.0, -83.2), // suroeste
  L.latLng(10.0, -77.0) // noreste
);

@Component({
  selector: 'app-mapa-dialog',
  templateUrl: './mapa-dialog.component.html',
  styleUrls: ['./mapa-dialog.component.scss']
})
export class MapaDialogComponent implements AfterViewInit {
  private cita: any = null;
  private map!: L.Map;
  private marker!: L.Marker;
  selectedLatLng: { lat: number; lng: number } | null = null;

  searchQuery: string = '';
  searchResults: any[] = [];
  private searchSubject = new Subject<string>();

  manualLat: number | null = null;
  manualLng: number | null = null;
  constructor(
    public dialogRef: MatDialogRef<MapaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { lat?: number; lng?: number; provincia?: Provincia, cita: any },
    private http: HttpClient,
    private localSave: LocalSaveService,
    private gateway: GatewayService,
    private snackbar: MatSnackBar
  ) {
    // 🕒 Configura búsqueda con debounce (espera 400ms entre teclas)
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((query) => this.buscarNominatim(query))
    ).subscribe((resultados) => {
      this.searchResults = resultados;
    });
  }

  ngAfterViewInit(): void {
    this.cita = this.data.cita;
    let lat = this.data.lat || 8.983333;
    let lng = this.data.lng || -79.516667;
    let zoom = 7;

    if (this.data.provincia && PROVINCIA_COORDENADAS[this.data.provincia.idprovincia]
      && !this.data.lat && !this.data.lng
    ) {
      const coord = PROVINCIA_COORDENADAS[this.data.provincia.idprovincia];
      lat = coord.lat;
      lng = coord.lng;
      zoom = coord.zoom;
    } else {
      lat = this.data.lat || lat;
      lng = this.data.lng || lng;
      zoom = 17;
    }

    if (this.cita) {
      this.map = L.map('map-dialog').setView([lat, lng], zoom);
    } else {
      // (LIMITAR)
      this.map = L.map('map-dialog', {
        maxBounds: PANAMA_BOUNDS,
        maxBoundsViscosity: 1.0
      }).setView([lat, lng], zoom);
    }

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap'
    }).addTo(this.map);

    if (this.data.provincia || (this.data.lat && this.data.lng)) {
      this.addOrUpdateMarker(lat, lng);
    }

    if (this.cita) {
      this.map.on('click', (e: any) => {
        const { lat, lng } = e.latlng;
        this.addOrUpdateMarker(lat, lng);
      });
    } else {
      // (LIMITAR) Evento click validado
      this.map.on('click', (e: any) => {
        const { lat, lng } = e.latlng;
        if (!PANAMA_BOUNDS.contains([lat, lng])) {
          this.snackbar.open('Ubicación fuera de Panamá', 'Cerrar', { duration: 3000 });
          return;
        }
        this.addOrUpdateMarker(lat, lng);
      });
    }

  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove(); // elimina listeners, capas y el mapa del DOM
    }
  }

  centrarEnCoordenadas(): void {
    if (this.manualLat !== null && this.manualLng !== null) {
      const lat = this.manualLat;
      const lng = this.manualLng;

      if (!this.cita) {
        // (LIMITAR) Verificar que no se mueva del pais de PANAMA
        if (!PANAMA_BOUNDS.contains([lat, lng])) {
          this.snackbar.open('Coordenadas fuera de Panamá', 'Cerrar', { duration: 3000 });
          return;
        }
      }

      this.map.setView([lat, lng], 17); // centra el mapa
      this.addOrUpdateMarker(lat, lng); // actualiza o crea el marcador
    }
  }

  private addOrUpdateMarker(lat: number, lng: number): void {
    this.selectedLatLng = { lat, lng };
    let popupContent = '';
    if (this.cita) {
      popupContent = `
        <b>${this.cita.status_name}</b><br>
        <b>ID Cita:</b> ${this.cita.idcita || ''}<br>
        <b>Prioridad:</b> ${this.cita.prioridad || ''}<br>
        <b>Provincia:</b> ${this.cita.provincia || ''}<br>
        <b>Dirección:</b> ${this.cita.address || ''}<br>
        <b>Técnico:</b> ${this.cita.tecnico || ''}<br>
        <b>Teléfono:</b> ${this.cita.telefono || ''}<br>
        <b>Contrata:</b> ${this.cita.contrata || ''}<br>
        <b>Fecha:</b> ${this.cita.date || ''} ${this.cita.am_pm || ''}<br>
        <b>Falla:</b> ${this.cita.falla || ''}<br>
        <b>Supervisor:</b> ${this.cita.supervisor || ''}<br>
        <b>Tel. Supervisor:</b> ${this.cita.tel_supervisor || ''}<br>
        <b>Jefe:</b> ${this.cita.jefe || ''}<br>
        <b>Tel. Jefe:</b> ${this.cita.tel_jefe || ''}<br>
        <b>Lat:</b> ${lat.toFixed(6)}<br>
        <b>Lng:</b> ${lng.toFixed(6)}`;
    } else {
      popupContent = `Lat: ${lat.toFixed(6)}<br>Lng: ${lng.toFixed(6)}`;
    }

    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
      this.marker.setPopupContent(popupContent).openPopup();
    } else {
      this.marker = L.marker([lat, lng], { draggable: true })
        .addTo(this.map)
        .bindPopup(popupContent)
        .openPopup();

      this.marker.on('dragend', (event: any) => {
        const pos = event.target.getLatLng();
        this.map.panTo(pos);
        this.addOrUpdateMarker(pos.lat, pos.lng);
      });
    }
  }

  // 🧠 Al escribir en el input
  onSearchChange(query: string): void {
    if (query.trim().length > 2) {
      this.searchSubject.next(query);
    } else {
      this.searchResults = [];
    }
  }

  // 🔍 Llamada HTTP al servicio de Nominatim
  private buscarNominatim(query: string) {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5`;
    return this.http.get<any[]>(url);
  }

  // 📍 Al seleccionar una sugerencia
  onOptionSelected(selected: any): void {
    const lat = parseFloat(selected.lat);
    const lng = parseFloat(selected.lon);

    // (LIMITAR)
    if (!PANAMA_BOUNDS.contains([lat, lng])) {
      this.snackbar.open('La ubicación seleccionada está fuera de Panamá', 'Cerrar', {
        duration: 4000
      });

      this.searchQuery = typeof selected.display_name === 'string'
      ? selected.display_name
      : '';

      return;
    }

    this.map.setView([lat, lng], 17);
    this.addOrUpdateMarker(lat, lng);
    this.searchQuery = selected.display_name;
    this.searchResults = [];
  }

  confirmarUbicacion(): void {
    if (this.cita) {
      const params = {
        ...this.cita,
        LATITUD: this.selectedLatLng.lat,
        LONGITUD: this.selectedLatLng.lng,
        IDDISTRIBUIDOR: this.localSave.get('usuarioPGV').IDU,
      }
      this.gateway.get('updateUbicacionCita', params).then((result) => {
        let aux: any = result;
        this.snackbar.open(aux.message, 'Cerrar', {
          duration: 5000
        });
      }
      ).catch(function (e) { });
    }
    this.dialogRef.close(this.selectedLatLng || null);
  }

  onNoClick() {
    this.dialogRef.close();
  }
}
